import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'

import { useApiClient } from 'clients/yogi-api'
import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import { useProjectStore } from 'features/project/store'
import IssueFeedbackModal from 'features/trend-detection/components/IssueFeedbackModal'
import { useIssueFeedback } from 'features/trend-detection/components/use-issue-feedback'
import { IssueFeedView } from 'features/trend-detection/features/issue-feed/components/IssueFeedView'
import { useFeedIssueCharts } from 'features/trend-detection/features/issue-feed/hooks/use-feed-issue-charts'
import { useFeedUpdateCharts } from 'features/trend-detection/features/issue-feed/hooks/use-feed-update-charts'
import { useIssueFeed } from 'features/trend-detection/features/issue-feed/hooks/use-issue-feed'
import { useKeyInsights } from 'features/trend-detection/features/issue-feed/hooks/use-key-insights'
import type {
  IssuesFeedSort,
  UpdatesFeedSort,
} from 'features/trend-detection/features/issue-feed/sort-types'
import {
  type AgentEvent,
  useAgentEventSubscription,
} from 'features/trend-detection/hooks/use-agent-events-stream'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentCreatePath,
  trendDetectionAgentsPath,
  trendDetectionBenchmarkingInsightPath,
} from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type {
  IssueResponse,
  IssueUpdateResponse,
  TrendFeedBannerAgent,
  TrendFeedIssueItem,
} from 'features/trend-detection/types'
import { trackChameleon } from 'trackers/chameleon'
import type { ProjectState } from 'types/project'

function extractPendingBanner(
  state: unknown,
): TrendFeedBannerAgent | undefined {
  if (
    typeof state === 'object' &&
    state !== null &&
    'pendingBannerAgent' in state
  ) {
    return (state as { pendingBannerAgent: TrendFeedBannerAgent })
      .pendingBannerAgent
  }
  return undefined
}

function extractPendingBenchmarkingBanner(
  state: unknown,
): TrendFeedBannerAgent | undefined {
  if (
    typeof state === 'object' &&
    state !== null &&
    'pendingBenchmarkingBanner' in state
  ) {
    return (state as { pendingBenchmarkingBanner: TrendFeedBannerAgent })
      .pendingBenchmarkingBanner
  }
  return undefined
}

function bannerDismissKey(b: TrendFeedBannerAgent): string {
  return b.run_uuid ?? b.agent_uuid
}

function loadDismissedKeys(projectId: string): Set<string> {
  try {
    const stored = localStorage.getItem(`yogi_dismissed_runs_${projectId}`)
    if (stored) return new Set(JSON.parse(stored) as string[])
  } catch {
    // ignore parse/storage errors
  }
  return new Set()
}

function saveDismissedKeys(projectId: string, keys: Set<string>): void {
  try {
    localStorage.setItem(
      `yogi_dismissed_runs_${projectId}`,
      JSON.stringify([...keys]),
    )
  } catch {
    // ignore storage quota errors
  }
}

export const IssueFeed: React.FC = () => {
  const service = useTrendDetectionService()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { data: featureFlagsRaw } = useFeatureFlags()
  const featureFlags = featureFlagsRaw as FeatureFlagsResponse | undefined
  const hasBenchmarkingFlag = !!featureFlags?.['benchmarking_agent']
  // Flag key is `general_agent`, not `insight_agent` — it predates the rename
  // and carries live per-user overrides. A non-global flag with no override is
  // ABSENT from the response, never `false`, hence the coercion.
  const hasInsightAgentFlag = !!featureFlags?.['general_agent']

  const [feedSection, setFeedSection] = useState<'insights' | 'tracked'>(() => {
    // Detail pages can pass `state: { feedSection: 'tracked' }` on back-nav so
    // the user lands on the same tab they came from. Default is Insights.
    const fromState = (location.state as { feedSection?: string } | null)
      ?.feedSection
    return fromState === 'tracked' ? 'tracked' : 'insights'
  })

  // Deep-link seeds captured once from the URL at mount — the agents list
  // "View latest/earlier" CTA lands here with agent_uuid / agent_type /
  // insight_recency. The follow-up useEffect strips the params after
  // applying them.
  const [initialDeepLink] = useState(() => {
    const agentUuid = searchParams.get('agent_uuid')
    const agentTypeParam = searchParams.get('agent_type')
    const recency = searchParams.get('insight_recency')
    const initialPeriod: 'current' | 'all_time' | undefined =
      recency === 'earlier'
        ? 'all_time'
        : recency === 'latest'
          ? 'current'
          : undefined
    const initialSubsection:
      | 'key-insights'
      | 'issue'
      | 'benchmarking'
      | 'agent-insights'
      | null =
      agentTypeParam === 'benchmarking'
        ? 'benchmarking'
        : agentTypeParam === 'general'
          ? 'agent-insights'
          : agentUuid || recency
            ? 'issue'
            : null
    return {
      agentUuid,
      agentType: agentTypeParam,
      initialPeriod,
      initialSubsection,
    }
  })

  const [feedSubsection, setFeedSubsection] = useState<
    'key-insights' | 'issue' | 'benchmarking' | 'agent-insights'
  >(initialDeepLink.initialSubsection ?? 'key-insights')
  // The hook still keys lazy-load + cache by detected vs tracked. Tracked
  // section maps directly; Insights section (any sub-view) maps to detected
  // so issue queries warm up regardless of which sub-tab is active first.
  const feedTab: 'detected' | 'tracked' =
    feedSection === 'tracked' ? 'tracked' : 'detected'
  const pendingBanner = extractPendingBanner(location.state)
  const initialBannerAgents = useMemo(
    () => (pendingBanner ? [pendingBanner] : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only read on mount
    [],
  )
  // Deep-link seeds are applied via useIssueFeed's initializer params so the
  // first render already has the filter + period set (no wasted unfiltered
  // fetch). Only the issue subsection consumes the agent filter here;
  // benchmarking + general subsections handle their own filters.
  const issueFeedInitialFilterAgentUuid =
    initialDeepLink.agentUuid &&
    initialDeepLink.agentType !== 'benchmarking' &&
    initialDeepLink.agentType !== 'general'
      ? initialDeepLink.agentUuid
      : null
  const feed = useIssueFeed(
    service,
    projectId ?? undefined,
    feedTab,
    initialBannerAgents,
    issueFeedInitialFilterAgentUuid,
    initialDeepLink.initialPeriod ?? 'current',
  )

  // Strip the deep-link params from the URL after the initial render so
  // subsequent user interactions own state cleanly (a refresh should not
  // re-apply the deep link).
  const strippedUrlParamsRef = useRef(false)
  useEffect(() => {
    if (strippedUrlParamsRef.current) return
    if (!initialDeepLink.agentUuid && !initialDeepLink.initialPeriod) return
    strippedUrlParamsRef.current = true
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('agent_uuid')
        next.delete('agent_type')
        next.delete('insight_recency')
        return next
      },
      { replace: true },
    )
  }, [initialDeepLink, setSearchParams])

  const [benchmarkingBanners, setBenchmarkingBanners] = useState<
    TrendFeedBannerAgent[]
  >([])
  const [dismissedRunKeys, setDismissedRunKeys] = useState<Set<string>>(() =>
    projectId ? loadDismissedKeys(projectId) : new Set(),
  )
  const autoDismissTimerRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map())
  const pendingBenchmarkingBanner = useMemo(
    () => extractPendingBenchmarkingBanner(location.state),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only read on mount
    [],
  )
  useEffect(() => {
    if (pendingBenchmarkingBanner) {
      setBenchmarkingBanners([pendingBenchmarkingBanner])
    }
  }, [pendingBenchmarkingBanner])

  // Resolve a single active benchmarking banner: fetch the run to learn whether
  // it finished, then drop the banner (and, on completion, refresh the feed).
  // The completion/failure TOAST is owned by the app-wide `useAgentRunToasts`
  // handler (mounted in ProjectLayout) so it fires exactly once — this resolver
  // is now banner-UI-only and never toasts.
  const resolveBenchmarkingRun = useCallback(
    async (runUuid: string): Promise<void> => {
      if (!projectId) return
      try {
        const { data } = await apiClient.get(
          `/benchmarking/runs/${runUuid}?proj_uuid=${projectId}`,
        )
        const { status } = data as { status: string }
        if (status === 'completed') {
          await queryClient.invalidateQueries([
            'benchmarking-insights-feed',
            projectId,
          ])
          setBenchmarkingBanners((prev) =>
            prev.filter((b) => b.run_uuid !== runUuid),
          )
        } else if (status === 'failed') {
          setBenchmarkingBanners((prev) =>
            prev.filter((b) => b.run_uuid !== runUuid),
          )
        }
      } catch {
        // Silently ignore transient errors; the banner stays until the next
        // event or a page refresh re-resolves it.
      }
    },
    [projectId, apiClient, queryClient],
  )

  // On each forwarded benchmarking event, resolve the matching active banner.
  // One connection (ProjectLayout) fans events here, so there is no extra
  // request storm — a banner resolves the instant its run transitions.
  const onAgentEvent = useCallback(
    (event: AgentEvent) => {
      if (event.agent_kind !== 'benchmarking') return
      const banner = benchmarkingBanners.find(
        (b) => b.run_uuid === event.run_uuid,
      )
      if (banner?.run_uuid) {
        void resolveBenchmarkingRun(banner.run_uuid)
      }
    },
    [benchmarkingBanners, resolveBenchmarkingRun],
  )
  // The agent-events SSE connection is owned app-wide by ProjectLayout. This
  // page only subscribes to the forwarded events (via the module-level fan-out)
  // to resolve its active benchmarking banners — it no longer opens a stream.
  useAgentEventSubscription(onAgentEvent)

  // Resolve each active banner once when it first appears — covers the case
  // where the run already finished before the page mounted (the SSE event for
  // it has already passed, so the subscription above would never fire for it).
  const resolvedBannerRunsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!projectId) return
    for (const { run_uuid } of benchmarkingBanners) {
      if (!run_uuid || resolvedBannerRunsRef.current.has(run_uuid)) continue
      resolvedBannerRunsRef.current.add(run_uuid)
      void resolveBenchmarkingRun(run_uuid)
    }
  }, [benchmarkingBanners, projectId, resolveBenchmarkingRun])

  // Start a 15s auto-dismiss timer the first time each banner appears.
  // Both manual X and auto-dismiss write to localStorage so the dismissal
  // survives navigation and refresh.
  useEffect(() => {
    const combined = [...feed.bannerAgents, ...benchmarkingBanners]
    for (const b of combined) {
      const key = bannerDismissKey(b)
      if (!autoDismissTimerRef.current.has(key)) {
        const timer = setTimeout(() => {
          setDismissedRunKeys((prev) => {
            const next = new Set([...prev, key])
            if (projectId) saveDismissedKeys(projectId, next)
            return next
          })
          autoDismissTimerRef.current.delete(key)
        }, 15_000)
        autoDismissTimerRef.current.set(key, timer)
      }
    }
    const activeKeys = new Set(combined.map(bannerDismissKey))
    for (const [key, timer] of autoDismissTimerRef.current) {
      if (!activeKeys.has(key)) {
        clearTimeout(timer)
        autoDismissTimerRef.current.delete(key)
      }
    }
  }, [feed.bannerAgents, benchmarkingBanners, projectId])

  useEffect(() => {
    if (!projectId) return
    setDismissedRunKeys(loadDismissedKeys(projectId))
    for (const timer of autoDismissTimerRef.current.values()) {
      clearTimeout(timer)
    }
    autoDismissTimerRef.current.clear()
  }, [projectId])

  const handleDismissBanner = useCallback(
    (dismissKey: string) => {
      const timer = autoDismissTimerRef.current.get(dismissKey)
      if (timer !== undefined) {
        clearTimeout(timer)
        autoDismissTimerRef.current.delete(dismissKey)
      }
      setDismissedRunKeys((prev) => {
        const next = new Set([...prev, dismissKey])
        if (projectId) saveDismissedKeys(projectId, next)
        return next
      })
    },
    [projectId],
  )

  const visibleBannerAgents = useMemo(
    () =>
      [...feed.bannerAgents, ...benchmarkingBanners].filter(
        (b) => !dismissedRunKeys.has(bannerDismissKey(b)),
      ),
    [feed.bannerAgents, benchmarkingBanners, dismissedRunKeys],
  )

  const handleRefresh = useCallback(async (): Promise<void> => {
    await feed.refetchFeed()
  }, [feed])
  const issueFeedViewTrackedProjectRef = useRef<string | null>(null)

  const setIssuesForFeedback = useCallback(
    (updater: React.SetStateAction<IssueResponse[]>) => {
      feed.setIssuesItems((prev) => {
        const issues = prev.map((p) => p.issue)
        const next =
          typeof updater === 'function'
            ? (updater as (i: IssueResponse[]) => IssueResponse[])(issues)
            : updater
        const byUuid = new Map(next.map((i) => [i.uuid, i]))
        return prev.map((it) => ({
          ...it,
          issue: byUuid.get(it.issue.uuid) ?? it.issue,
        }))
      })
    },
    [feed],
  )

  const setUpdatesForFeedback = useCallback(
    (updater: React.SetStateAction<IssueUpdateResponse[]>) => {
      feed.setUpdatesItems((prev) => {
        // Feedback flows through the update payload, so skip freshly-tracked
        // items that don't have one yet — they'll pick up feedback once the
        // next scheduled run produces an update for the source.
        const updates = prev
          .map((p) => p.update)
          .filter((u): u is IssueUpdateResponse => u !== null)
        const next =
          typeof updater === 'function'
            ? (updater as (u: IssueUpdateResponse[]) => IssueUpdateResponse[])(
                updates,
              )
            : updates
        const byUuid = new Map(next.map((u) => [u.uuid, u]))
        return prev.map((it) => ({
          ...it,
          update: it.update ? (byUuid.get(it.update.uuid) ?? it.update) : null,
        }))
      })
    },
    [feed],
  )

  const feedback = useIssueFeedback(
    service,
    projectId,
    setIssuesForFeedback,
    setUpdatesForFeedback,
  )

  useEffect(() => {
    if (!projectId) {
      issueFeedViewTrackedProjectRef.current = null
      return
    }
    if (issueFeedViewTrackedProjectRef.current === projectId) return
    issueFeedViewTrackedProjectRef.current = projectId
    trackResearchAgents(ResearchAgentsAction.insights_feed_view, {
      project_id: projectId,
      has_agents: feed.agentsForFilter.length > 0,
    })
    trackChameleon('Visited Insights Feed')
  }, [projectId, feed.agentsForFilter.length])

  const resolveFeedbackContext = useCallback(
    (issueUuid: string) => {
      const fromIssue = feed.issuesItems.find((i) => i.issue.uuid === issueUuid)
      if (fromIssue) {
        return {
          feed_tab: 'detected' as const,
          agent_uuid: fromIssue.agent_uuid,
          run_uuid: fromIssue.run_uuid,
        }
      }
      const fromUpdate = feed.updatesItems.find(
        (u) => u.source_issue.uuid === issueUuid,
      )
      if (fromUpdate) {
        return {
          feed_tab: 'tracked' as const,
          agent_uuid: fromUpdate.agent_uuid,
          run_uuid: fromUpdate.run_uuid,
        }
      }
      return {
        feed_tab: feedTab,
        agent_uuid: undefined as string | undefined,
        run_uuid: undefined as string | undefined,
      }
    },
    [feed.issuesItems, feed.updatesItems, feedTab],
  )

  const handleFeedSectionChange = useCallback(
    (section: 'insights' | 'tracked') => {
      if (section === feedSection) return
      trackResearchAgents(ResearchAgentsAction.feed_tab_change, {
        tab: section === 'insights' ? 'feed' : 'tracked',
      })
      setFeedSection(section)
    },
    [feedSection],
  )

  const handleFeedSubsectionChange = useCallback(
    (
      subsection: 'key-insights' | 'issue' | 'benchmarking' | 'agent-insights',
    ) => {
      if (subsection === feedSubsection) return
      const subsectionLabel =
        subsection === 'key-insights'
          ? 'key_insights'
          : subsection === 'issue'
            ? 'emerging_issues'
            : subsection === 'agent-insights'
              ? 'agent_insights'
              : 'performance_gaps'
      trackResearchAgents(ResearchAgentsAction.feed_subsection_change, {
        subsection: subsectionLabel,
      })
      setFeedSubsection(subsection)
    },
    [feedSubsection],
  )

  const onIssuesSortChangeTracked = useCallback(
    (sort: IssuesFeedSort) => {
      feed.onIssuesSortChange(sort)
      trackResearchAgents(ResearchAgentsAction.emerging_issues_sort_change, {
        sort_by: sort,
      })
    },
    [feed],
  )

  const onUpdatesSortChangeTracked = useCallback(
    (sort: UpdatesFeedSort) => {
      feed.onUpdatesSortChange(sort)
      trackResearchAgents(ResearchAgentsAction.tracked_sort_change, {
        sort_by: sort,
      })
    },
    [feed],
  )

  const onPeriodChangeTracked = useCallback(
    (
      next: 'current' | 'all_time',
      source: 'toolbar' | 'view_previous_banner',
    ) => {
      if (next === feed.period) return
      trackResearchAgents(ResearchAgentsAction.emerging_issues_period_change, {
        period: next,
        source,
      })
      feed.onPeriodChange(next)
    },
    [feed],
  )

  const onFilterAgentChangeTracked = useCallback(
    (uuid: string | null) => {
      feed.setFilterAgentUuid(uuid)
      trackResearchAgents(ResearchAgentsAction.feed_agent_filter_change, {
        agent_uuid: uuid ?? 'all',
      })
    },
    [feed],
  )

  const onElevationFilterChangeTracked = useCallback(
    (level: string | null) => {
      feed.onEmergenceLevelFilterChange(level)
      trackResearchAgents(
        ResearchAgentsAction.emerging_issues_elevation_filter_change,
        { level: level ?? null },
      )
    },
    [feed],
  )

  const onOpenAgentCenter = useCallback(() => {
    if (!projectId) return
    trackResearchAgents(ResearchAgentsAction.feed_navigate_agent_center, {})
    navigate(trendDetectionAgentCreatePath(projectId))
  }, [navigate, projectId])

  const onManageAgents = useCallback(() => {
    if (!projectId) return
    navigate(trendDetectionAgentsPath(projectId))
  }, [navigate, projectId])

  const onOpenAgentFromIssueCard = useCallback(
    (ctx: { agentUuid: string; agentName: string }) => {
      trackResearchAgents(ResearchAgentsAction.open_agent_config, {
        source: 'issue_feed_card',
        agent_uuid: ctx.agentUuid,
        agent_name: ctx.agentName,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
      })
    },
    [],
  )

  const handleFeedbackCancelTracked = useCallback(() => {
    const issueUuid = feedback.feedbackIssueUuid
    const isPositive = feedback.feedbackIsPositive
    if (issueUuid !== null && isPositive !== null) {
      const ctx = resolveFeedbackContext(issueUuid)
      trackResearchAgents(ResearchAgentsAction.feedback_modal_dismiss, {
        issue_uuid: issueUuid,
        agent_uuid: ctx.agent_uuid,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        run_uuid: ctx.run_uuid,
        is_positive: isPositive,
        feed_tab: ctx.feed_tab,
      })
    }
    feedback.handleFeedbackCancel()
  }, [feedback, resolveFeedbackContext])

  const handleFeedbackSubmitWithToast = useCallback(
    async (isPositive: boolean, additionalDetails: string) => {
      const issueUuid = feedback.feedbackIssueUuid
      try {
        await feedback.handleFeedbackSubmit(isPositive, additionalDetails)
        if (issueUuid) {
          const ctx = resolveFeedbackContext(issueUuid)
          trackResearchAgents(ResearchAgentsAction.feedback_submit, {
            issue_uuid: issueUuid,
            agent_uuid: ctx.agent_uuid,
            agent_type: ISSUE_DETECTION_AGENT_TYPE,
            run_uuid: ctx.run_uuid,
            is_positive: isPositive,
            has_additional_details: additionalDetails.trim().length > 0,
            feed_tab: ctx.feed_tab,
          })
        }
        toast.success('Feedback submitted')
      } catch {
        toast.error('Failed to submit feedback')
      }
    },
    [feedback, resolveFeedbackContext],
  )

  const { keyInsights, isLoading: keyInsightsLoading } =
    useKeyInsights(projectId)

  // Key Insights items are issue rows from the same agents/runs as the main
  // feed, but may not appear on the current page. Combine the two sources
  // (deduped by issue uuid) before fetching timeseries charts so cards on
  // Key Insights get the same per-issue and per-run-total charts the Issue
  // Insights cards do. The chart hook itself keys by uuid, so duplicates
  // across the two lists don't cause double fetches.
  const chartFeedItems = useMemo(() => {
    const seen = new Set<string>()
    const combined: TrendFeedIssueItem[] = []
    for (const item of feed.issuesItems) {
      seen.add(item.issue.uuid)
      combined.push(item)
    }
    for (const ki of keyInsights) {
      if (ki.type !== 'issue') continue
      if (seen.has(ki.issue.uuid)) continue
      seen.add(ki.issue.uuid)
      combined.push({
        agent_uuid: ki.agent_uuid,
        agent_name: ki.agent_name,
        run_uuid: ki.run_uuid,
        issue: ki.issue,
      })
    }
    return combined
  }, [feed.issuesItems, keyInsights])
  const issueCharts = useFeedIssueCharts(service, projectId, chartFeedItems)
  const updateCharts = useFeedUpdateCharts(
    service,
    projectId,
    feed.updatesItems,
  )

  // No-update tracked items have no `update.uuid` to key against, so the
  // update-chart hook skips them. Fetch their source-issue chart instead and
  // pass it through as the fallback for the in-between "tracked but not yet
  // updated" state — same shape as the Emerging Issues card.
  const trackedSourceItems = useMemo<TrendFeedIssueItem[]>(
    () =>
      feed.updatesItems
        .filter((u) => !u.update)
        .map((u) => ({
          agent_uuid: u.agent_uuid,
          agent_name: u.agent_name,
          run_uuid: u.run_uuid,
          issue: u.source_issue,
        })),
    [feed.updatesItems],
  )
  const trackedSourceCharts = useFeedIssueCharts(
    service,
    projectId,
    trackedSourceItems,
  )

  if (!projectId) return null

  return (
    <>
      <IssueFeedView
        projectId={projectId}
        bannerAgents={visibleBannerAgents}
        onDismissBanner={handleDismissBanner}
        agentsForFilter={feed.agentsForFilter}
        filterAgentUuid={feed.filterAgentUuid}
        onFilterAgentChange={onFilterAgentChangeTracked}
        issuesItems={feed.issuesItems}
        updatesItems={feed.updatesItems}
        issuesTotalCount={feed.issuesTotalCount}
        updatesTotalCount={feed.updatesTotalCount}
        issuesPage={feed.issuesPage}
        updatesPage={feed.updatesPage}
        issuesTotalPages={feed.issuesTotalPages}
        updatesTotalPages={feed.updatesTotalPages}
        onIssuesPageChange={feed.onIssuesPageChange}
        onUpdatesPageChange={feed.onUpdatesPageChange}
        onOpenSettings={onOpenAgentCenter}
        onManageAgents={onManageAgents}
        issuesFeedLoading={feed.issuesFeedLoading}
        updatesFeedLoading={feed.updatesFeedLoading}
        issuesSort={feed.issuesSort}
        updatesSort={feed.updatesSort}
        onIssuesSortChange={onIssuesSortChangeTracked}
        onUpdatesSortChange={onUpdatesSortChangeTracked}
        isShared={false}
        feedIssueCards={issueCharts.feedIssueCards}
        chartDataMap={issueCharts.chartDataMap}
        chartLoadingMap={issueCharts.chartLoadingMap}
        projectTotalChartMap={issueCharts.projectTotalChartMap}
        updateTimeseriesCharts={updateCharts.updateTimeseriesCharts}
        updateTimeseriesLoadingMap={updateCharts.updateTimeseriesLoadingMap}
        updateProjectTotalChartMap={updateCharts.updateProjectTotalChartMap}
        trackedSourceChartMap={trackedSourceCharts.chartDataMap}
        trackedSourceChartLoadingMap={trackedSourceCharts.chartLoadingMap}
        trackedSourceProjectTotalChartMap={
          trackedSourceCharts.projectTotalChartMap
        }
        feedSection={feedSection}
        onFeedSectionChange={handleFeedSectionChange}
        feedSubsection={feedSubsection}
        onFeedSubsectionChange={handleFeedSubsectionChange}
        onOpenAgentFromIssueCard={onOpenAgentFromIssueCard}
        dataUpdatedAt={feed.dataUpdatedAt}
        onRefresh={handleRefresh}
        isRefreshing={feed.isRefetching}
        hasBenchmarkingFeed={hasBenchmarkingFlag}
        hasAgentInsightsFeed={hasInsightAgentFlag}
        benchmarkingInitialAgentUuidFilter={
          initialDeepLink.agentType === 'benchmarking'
            ? initialDeepLink.agentUuid
            : null
        }
        benchmarkingInitialPeriod={initialDeepLink.initialPeriod}
        keyInsights={keyInsights}
        keyInsightsLoading={keyInsightsLoading}
        onBenchmarkingInsightClick={(insight) => {
          trackResearchAgents(ResearchAgentsAction.benchmarking_insight_click, {
            insight_uuid: insight.uuid,
            insight_title: insight.title,
            agent_uuid: insight.agent_uuid,
            agent_name: insight.agent_name,
            source: 'key_insights',
          })
          if (projectId) {
            navigate(
              trendDetectionBenchmarkingInsightPath(projectId, insight.uuid),
            )
          }
        }}
        period={feed.period}
        onPeriodChange={(p) => onPeriodChangeTracked(p, 'toolbar')}
        onViewPreviousIssues={() =>
          onPeriodChangeTracked('all_time', 'view_previous_banner')
        }
        sharplyElevatedCount={feed.sharplyElevatedCount}
        hasHistoricalIssues={feed.hasHistoricalIssues}
        emergenceLevelFilter={feed.emergenceLevelFilter}
        onEmergenceLevelFilterChange={onElevationFilterChangeTracked}
      />
      <IssueFeedbackModal
        isVisible={feedback.feedbackModalVisible}
        isPositive={feedback.feedbackIsPositive}
        onSubmit={handleFeedbackSubmitWithToast}
        onCancel={handleFeedbackCancelTracked}
        isLoading={feedback.submittingFeedback}
      />
    </>
  )
}
